import React, { useEffect, useState, useContext, useRef } from 'react';
import { FlowChartContext } from '../../app/providers/FlowChartContext';
import FlowChartDevelopmentScreen from './FlowChartDevelopmentScreen';
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { useNavigate } from 'react-router-dom';
import mxgraph from 'mxgraph';

const { mxGraph, mxClient, mxEvent, mxUtils, mxPoint, mxConnectionConstraint, mxConstants, mxShape, mxCellRenderer } = mxgraph();

// Custom Triangle Shape
function mxTriangleShape() {
  mxShape.call(this);
}
mxUtils.extend(mxTriangleShape, mxShape);
mxTriangleShape.prototype.paintVertexShape = function(c, x, y, w, h) {
  c.translate(x, y);
  c.begin();
  c.moveTo(w / 2, 0);
  c.lineTo(w, h);
  c.lineTo(0, h);
  c.close();
  c.fillAndStroke();
};
mxTriangleShape.prototype.getLabelBounds = function(rect) {
  const bounds = new mxPoint(rect.x, rect.y);
  bounds.width = rect.width;
  bounds.height = rect.height;
  bounds.y += rect.height * 0.25;
  bounds.height *= 0.75;
  return bounds;
};

// Custom Predefined Process Shape
function mxPredefinedProcessShape() {
  mxShape.call(this);
}
mxUtils.extend(mxPredefinedProcessShape, mxShape);
mxPredefinedProcessShape.prototype.paintVertexShape = function(c, x, y, w, h) {
  c.translate(x, y);
  const inset = 8;
  c.begin();
  c.moveTo(0, 0);
  c.lineTo(w, 0);
  c.lineTo(w, h);
  c.lineTo(0, h);
  c.close();
  c.moveTo(inset, 0);
  c.lineTo(inset, h);
  c.moveTo(w - inset, 0);
  c.lineTo(w - inset, h);
  c.fillAndStroke();
};

const FlowChart = ({ workflowData }) => {
  // Context
  const { setWorkflowData, setRequestFiles } = useContext(FlowChartContext);
  const navigate = useNavigate();
  // Development Mode State
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(false);
  
  // Preview graph refs
  const previewGraphRef = useRef(null);
  const previewContainerRef = useRef(null);
  
  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showRuler, setShowRuler] = useState(true);
  
  // Workflow ID'yi ref ile takip et - sadece ID değiştiğinde context'i güncelle
  const workflowIdRef = useRef(null);

  // ✅ Button Styles - Dark Theme
  const compactButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    fontSize: 16,
    fontWeight: 600,
  };

  const iconButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '6px 8px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  };

  // Zoom fonksiyonları
  const handleZoomIn = () => {
    if (!previewGraphRef.current) return;
    const graph = previewGraphRef.current;
    const newScale = Math.min(graph.view.scale * 1.2, 3); // Max 300%
    graph.zoomTo(newScale);
    setZoomLevel(Math.round(newScale * 100));
  };

  const handleZoomOut = () => {
    if (!previewGraphRef.current) return;
    const graph = previewGraphRef.current;
    const newScale = Math.max(graph.view.scale / 1.2, 0.1); // Min 10%
    graph.zoomTo(newScale);
    setZoomLevel(Math.round(newScale * 100));
  };

  const handleZoomReset = () => {
    if (!previewGraphRef.current) return;
    const graph = previewGraphRef.current;
    graph.zoomTo(1);
    graph.view.translate.x = 0;
    graph.view.translate.y = 0;
    graph.view.revalidate();
    setZoomLevel(100);
  };

  const handleFitScreen = () => {
    if (!previewGraphRef.current || !previewContainerRef.current) return;
    const graph = previewGraphRef.current;
    const container = previewContainerRef.current;
    
    try {
      const bounds = graph.getGraphBounds();
      if (bounds && bounds.width > 0 && bounds.height > 0) {
        const containerWidth = container.clientWidth || 800;
        const containerHeight = container.clientHeight || 600;
        
        const padding = 40;
        const scaleX = (containerWidth - padding * 2) / bounds.width;
        const scaleY = (containerHeight - padding * 2) / bounds.height;
        const scale = Math.min(scaleX, scaleY, 1);
        
        graph.view.scale = scale;
        
        const dx = (containerWidth - bounds.width * scale) / 2 - bounds.x * scale;
        const dy = (containerHeight - bounds.height * scale) / 2 - bounds.y * scale;
        
        graph.view.translate.x = dx / scale;
        graph.view.translate.y = dy / scale;
        
        graph.view.revalidate();
        graph.refresh();
        setZoomLevel(Math.round(scale * 100));
      }
    } catch (error) {
      // ignore
    }
  };

  // Save workflowData to context - only when workflow ID actually changes
  useEffect(() => {
    const currentWorkflowId = workflowData?.workflow?.id || null;
    const workflowIdChanged = workflowIdRef.current !== currentWorkflowId;
    
    if (workflowIdChanged && workflowData && setWorkflowData) {
      setWorkflowData(workflowData);
      workflowIdRef.current = currentWorkflowId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowData?.workflow?.id]); // Sadece workflow ID'yi dinle

  // Request files API call - workflowData render olduğunda
  useEffect(() => {
    if (!workflowData || !workflowData.REQID) return;
    
    const fetchRequestFiles = async () => {
      try {
        const processId = process.env.REACT_APP_GET_REQUEST || '100023233'; // Default process ID
        if (!processId) {
          console.warn('REACT_APP_GET_REQUEST environment variable tanımlı değil!');
          return;
        }
        
        const token = localStorage.getItem('token');
        const bodyParams = {
          request_id: workflowData.REQID,
          process: processId,
        };
        
        const response = await fetchWrapper(processId, bodyParams, navigate);
        
        // Response'u kontrol et
        if (response && response.result && Array.isArray(response.result)) {
          setRequestFiles(response.result);
        } else {
          console.warn('Request files response formatı beklenen formatta değil:', response);
          setRequestFiles(null);
        }
      } catch (error) {
        console.error('Request files fetch hatası:', error);
        setRequestFiles(null);
      }
    };
    
    fetchRequestFiles();
  }, [workflowData?.REQID, navigate, setRequestFiles]);

  // Preview Mode: Read-only flowchart render
  useEffect(() => {
    if (!workflowData || !previewContainerRef.current || previewGraphRef.current) return;

    const container = previewContainerRef.current;
    if (!container) return;

    if (!mxClient.isBrowserSupported()) {
      mxUtils.error('Browser not supported!', 200, false);
      return;
    }

    const graph = new mxGraph(container);
    previewGraphRef.current = graph;

    // Register custom shapes
    mxCellRenderer.registerShape('triangle', mxTriangleShape);
    mxCellRenderer.registerShape('predefinedProcess', mxPredefinedProcessShape);

    // Read-only settings - sadece panning ve zoom
    graph.setEnabled(false);
    graph.setCellsMovable(false);
    graph.setConnectable(false);
    graph.setCellsEditable(false);
    graph.setCellsResizable(false);
    graph.setCellsBendable(false);
    graph.setCellsDisconnectable(false);
    graph.setAllowDanglingEdges(false);
    graph.setPanning(true);
    graph.panningHandler.useLeftButtonForPanning = true;
    graph.panningHandler.useMiddleButtonForPanning = true;
    graph.panningHandler.ignoreCell = true;
    graph.gridSize = 20;
    graph.gridEnabled = true;
    graph.setTooltips(false);
    graph.centerZoom = true;

    // ✅ Mouse wheel zoom desteği
    mxEvent.addMouseWheelListener((evt, up) => {
      if (evt.ctrlKey || evt.metaKey) {
        const currentScale = graph.view.scale;
        const newScale = up 
          ? Math.min(currentScale * 1.2, 3) 
          : Math.max(currentScale / 1.2, 0.1);
        graph.zoomTo(newScale);
        setZoomLevel(Math.round(newScale * 100));
        mxEvent.consume(evt);
      }
    }, container);

    // Container style
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.background = `url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAyMCAwIEwgMCAwIDAgMjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2UwZTBlMCIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+') repeat`;
    container.style.backgroundColor = '#fafafa';
    
    // ✅ Dynamic cursor - panning sırasında değişir
    container.style.cursor = 'grab';
    container.addEventListener('mousedown', () => {
      container.style.cursor = 'grabbing';
    });
    container.addEventListener('mouseup', () => {
      container.style.cursor = 'grab';
    });

    // Load workflow data
    if (workflowData && workflowData.contents) {
      const model = graph.getModel();
      const parent = graph.getDefaultParent();
      const nodes = {};

      model.beginUpdate();
      try {
        // Render vertices
        workflowData.contents
          .filter(item => item && (item.vertex === "1" || item.vertex === 1 || item.vertex === true))
          .forEach(node => {
            const nodeId = node.id ? String(node.id) : '';
            const nodeStyle = node.style ? String(node.style) : '';
            const nodeDefinition = node.definition ? String(node.definition) : '';
            
            let style = nodeStyle || '';
            if (style && !style.endsWith(';')) {
              style += ';';
            }
            
            // Shape bilgisini style'dan çıkar
            let shapeNameFromStyle = '';
            const shapeMatch = style.match(/shape=([^;]+)/);
            if (shapeMatch) {
              shapeNameFromStyle = shapeMatch[1];
            }
            
            // Image'ları kaldır
            style = style.replace(/image=[^;]*;?/g, '');
            style = style.replace(/imageWidth=[^;]*;?/g, '');
            style = style.replace(/imageHeight=[^;]*;?/g, '');
            style = style.replace(/imageAspect=[^;]*;?/g, '');
            style = style.replace(/imageAlign=[^;]*;?/g, '');
            style = style.replace(/imageVerticalAlign=[^;]*;?/g, '');
            
            // Shape'i mxGraph'in built-in shape'lerine map et
            let mxShape = 'rectangle';
            if (shapeNameFromStyle === 'ellipse' || shapeNameFromStyle === 'startCircle' || shapeNameFromStyle === 'stopCircle') {
              mxShape = 'ellipse';
            } else if (shapeNameFromStyle === 'rhombus') {
              mxShape = 'rhombus';
            } else if (shapeNameFromStyle === 'triangle') {
              mxShape = 'triangle';
            } else if (shapeNameFromStyle === 'hexagon') {
              mxShape = 'hexagon';
            } else if (shapeNameFromStyle === 'cylinder') {
              mxShape = 'cylinder';
            } else if (shapeNameFromStyle === 'preProcess' || shapeNameFromStyle === 'predefinedProcess') {
              mxShape = 'predefinedProcess';
            } else if (shapeNameFromStyle === 'rectangle') {
              mxShape = 'rectangle';
            } else if (!shapeNameFromStyle || shapeNameFromStyle === 'image') {
              mxShape = 'rectangle';
            } else {
              mxShape = shapeNameFromStyle;
            }
            
            // Shape'i güncelle
            style = style.replace(/shape=[^;]*;?/g, '');
            if (!style.includes('fillColor')) {
              style += 'fillColor=#FFFF00;';
            }
            style = style.replace(/selectable=[^;]*;?/g, '');
            style = style.replace(/movable=[^;]*;?/g, '');
            style = style.replace(/resizable=[^;]*;?/g, '');
            style = style.replace(/connectable=[^;]*;?/g, '');
            style = style.replace(/;;+/g, ';');
            if (!style.endsWith(';')) style += ';';
            
            const x = parseInt(node.GeometryX) || 40;
            const y = parseInt(node.GeometryY) || 40;
            const w = parseInt(node.GeometryWidth) || 80;
            const h = parseInt(node.GeometryHeight) || 40;
            const label = nodeDefinition;
            
            style += `shape=${mxShape};`;
            style += 'selectable=0;movable=0;resizable=0;';
            
            nodes[nodeId] = graph.insertVertex(parent, nodeId, label, x, y, w, h, style);
          });

        // Render edges
        workflowData.contents
          .filter(item => item && (item.edge === "1" || item.edge === 1 || item.edge === true))
          .forEach(edge => {
            const edgeId = edge.id ? String(edge.id) : '';
            const edgeStyle = edge.style ? String(edge.style) : '';
            const edgeDefinition = edge.definition ? String(edge.definition) : '';
            const edgeKosul = edge.kosul ? String(edge.kosul) : '';
            const sourceId = edge.source ? String(edge.source) : '';
            const targetId = edge.target ? String(edge.target) : '';
            
            const from = nodes[sourceId];
            const to = nodes[targetId];
            if (from && to) {
              let style = edgeStyle;
              if (!style.includes('endArrow')) style += 'endArrow=block;';
              style += 'edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;';
              if (!style.includes('perimeter')) style += 'perimeter=1;';
              style = style.replace(/bendable=[^;]*;?/g, '');
              style = style.replace(/editable=[^;]*;?/g, '');
              style = style.replace(/selectable=[^;]*;?/g, '');
              style = style.replace(/movable=[^;]*;?/g, '');
              style = style.replace(/;;+/g, ';');
              if (!style.endsWith(';')) style += ';';
              
              style += 'bendable=0;editable=0;selectable=0;movable=0;';
              style += 'labelBackgroundColor=#fff;labelBorderColor=#e0e0e0;';
              
              let label = edgeDefinition;
              if (edgeKosul && !label.includes(':Next')) {
                label = `${edgeKosul}:Next`;
              }
              
              const e = graph.insertEdge(parent, edgeId, label, from, to, style);
              
              if (edge.GeometryPoints) {
                try {
                  const points = JSON.parse(edge.GeometryPoints);
                  if (Array.isArray(points) && points.length > 0) {
                    const geo = e.getGeometry().clone();
                    geo.points = points.map(pt => new mxPoint(pt.x, pt.y));
                    e.setGeometry(geo);
                  }
                } catch (err) {
                  // ignore
                }
              }
            }
          });
      } finally {
        model.endUpdate();
      }

      graph.view.validate();
      
      // Fit ve uzaktan göster - tüm flowchart'ı görünür yap
      try {
        const bounds = graph.getGraphBounds();
        if (bounds && bounds.width > 0 && bounds.height > 0) {
          const containerWidth = container.clientWidth || 800;
          const containerHeight = container.clientHeight || 600;
          
          // Padding ekle
          const padding = 40;
          const scaleX = (containerWidth - padding * 2) / bounds.width;
          const scaleY = (containerHeight - padding * 2) / bounds.height;
          const scale = Math.min(scaleX, scaleY, 1); // 1'den büyük olmasın (zoom out)
          
          // Scale'i uygula
          graph.view.scale = scale;
          
          // Merkeze hizala
          const dx = (containerWidth - bounds.width * scale) / 2 - bounds.x * scale;
          const dy = (containerHeight - bounds.height * scale) / 2 - bounds.y * scale;
          
          graph.view.translate.x = dx / scale;
          graph.view.translate.y = dy / scale;
          
          graph.view.revalidate();
          graph.refresh();
          
          // ✅ Initial zoom level'ı state'e kaydet
          setZoomLevel(Math.round(scale * 100));
        }
      } catch (error) {
        console.warn('Fit zoom error:', error);
      }
    }

    return () => {
      if (previewGraphRef.current) {
        try {
          previewGraphRef.current.destroy();
        } catch (error) {
          // ignore
        }
        previewGraphRef.current = null;
      }
    };
  }, [workflowData && workflowData.workflow && workflowData.workflow.id]);

  // ✅ Dynamically update rulers based on mxGraph view
  useEffect(() => {
    const graph = previewGraphRef.current;
    const container = previewContainerRef.current;
    if (!graph || !container || !showRuler) return;
    
    // Container'ın hazır olmasını bekle
    if (!container.clientWidth || !container.clientHeight) {
      setTimeout(() => {
        const leftRuler = document.getElementById('left-ruler-preview');
        const topRuler = document.getElementById('top-ruler-preview');
        if (leftRuler && topRuler) {
          // Force update after mount
          const event = new Event('resize');
          window.dispatchEvent(event);
        }
      }, 300);
    }

    const updateRulers = () => {
      try {
        const view = graph.view;
        if (!view) return;
        
        const scale = view.scale || 1;
        const translate = view.translate || new window.mxPoint(0, 0);
        const containerRect = container.getBoundingClientRect();
        const gridSize = graph.gridSize || 20;
        const scaledGrid = gridSize * scale;
        const startX = -translate.x * scale;
        const startY = -translate.y * scale;
        const width = containerRect.width;
        const height = containerRect.height;

        const leftRuler = document.getElementById('left-ruler-preview');
        const topRuler = document.getElementById('top-ruler-preview');
        
        if (!leftRuler || !topRuler) {
          return;
        }

        // Left Ruler (Vertical - Y coordinates) - Simplified
        if (leftRuler) {
          leftRuler.innerHTML = '';
          const step = 100 * scale;
          let count = 0;
          
          for (let i = 0; i < 50; i++) {
            const y = i * step;
            const pixelY = y - startY;
            
            if (pixelY >= 0 && pixelY <= height) {
              const value = i * 100;
              count++;
              
              // Tick mark
              const tick = document.createElement('div');
              tick.style.cssText = `position: absolute; top: ${pixelY}px; left: 0; width: 7px; height: 2px; background: #999;`;
              leftRuler.appendChild(tick);
              
              // Label
              const label = document.createElement('div');
              label.textContent = value.toString();
              label.style.cssText = `position: absolute; top: ${pixelY - 8}px; left: 10px; font-size: 9px; color: #ffffff; background: rgba(0,0,0,0.75); padding: 2px 4px; border-radius: 2px; line-height: 1; white-space: nowrap; font-weight: 700; font-family: monospace; z-index: 100;`;
              leftRuler.appendChild(label);
            }
          }
        }

        // Top Ruler (Horizontal - X coordinates) - Simplified
        if (topRuler) {
          topRuler.innerHTML = '';
          const step = 100 * scale;
          let count = 0;
          
          for (let i = 0; i < 50; i++) {
            const x = i * step;
            const pixelX = x - startX;
            
            if (pixelX >= 0 && pixelX <= width) {
              const value = i * 100;
              count++;
              
              // Tick mark
              const tick = document.createElement('div');
              tick.style.cssText = `position: absolute; left: ${pixelX}px; top: 0; width: 2px; height: 7px; background: #999;`;
              topRuler.appendChild(tick);
              
              // Label
              const label = document.createElement('div');
              label.textContent = value.toString();
              label.style.cssText = `position: absolute; left: ${pixelX + 4}px; top: 8px; font-size: 9px; color: #ffffff; background: rgba(0,0,0,0.75); padding: 2px 4px; border-radius: 2px; line-height: 1; white-space: nowrap; font-weight: 700; font-family: monospace; z-index: 100;`;
              topRuler.appendChild(label);
            }
          }
        }
      } catch (error) {
        // Ignore errors
      }
    };

    // Listen to mxGraph view changes
    const updateHandler = () => updateRulers();
    if (graph.view) {
      graph.view.addListener(window.mxEvent.SCALE, updateHandler);
      graph.view.addListener(window.mxEvent.SCALE_AND_TRANSLATE, updateHandler);
      graph.view.addListener(window.mxEvent.TRANSLATE, updateHandler);
    }

    // Initial update - multiple attempts to ensure rulers are drawn
    updateRulers(); // Immediate
    setTimeout(updateRulers, 50);
    setTimeout(updateRulers, 100);
    setTimeout(updateRulers, 200);
    setTimeout(updateRulers, 500);
    setTimeout(updateRulers, 1000);

    // Scroll listener
    const scrollHandler = () => updateRulers();
    if (container) {
      container.addEventListener('scroll', scrollHandler);
    }

    // Resize observer
    const resizeObserver = new ResizeObserver(() => {
      updateRulers();
    });
    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      if (graph.view) {
        try {
          graph.view.removeListener(window.mxEvent.SCALE, updateHandler);
          graph.view.removeListener(window.mxEvent.SCALE_AND_TRANSLATE, updateHandler);
          graph.view.removeListener(window.mxEvent.TRANSLATE, updateHandler);
        } catch (error) {
          // ignore
        }
      }
      if (container) {
        container.removeEventListener('scroll', scrollHandler);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [showRuler]);

  // ✅ Mouse wheel zoom with Ctrl/Cmd
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container || !previewGraphRef.current) return;

    const handleWheel = (evt) => {
      if (evt.ctrlKey || evt.metaKey) {
        evt.preventDefault();
        const delta = evt.deltaY;
        const graph = previewGraphRef.current;
        const view = graph.view;
        const scale = view.scale;

        if (delta < 0) {
          const newScale = Math.min(scale * 1.1, 3);
          view.scaleAndTranslate(newScale, view.translate.x, view.translate.y);
          setZoomLevel(Math.round(newScale * 100));
        } else {
          const newScale = Math.max(scale / 1.1, 0.1);
          view.scaleAndTranslate(newScale, view.translate.x, view.translate.y);
          setZoomLevel(Math.round(newScale * 100));
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // ✅ Pan mode with grab cursor
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    let isPanning = false;

    const handleMouseDown = (e) => {
      if (e.button === 0) {
        isPanning = true;
        container.style.cursor = 'grabbing';
      }
    };

    const handleMouseUp = () => {
      if (isPanning) {
        isPanning = false;
        container.style.cursor = 'grab';
      }
    };

    const handleMouseLeave = () => {
      if (isPanning) {
        isPanning = false;
        container.style.cursor = 'grab';
      }
    };

    container.style.cursor = 'grab';
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // ========== RENDER ==========
  return (
    <>
      {/* Flowchart Preview Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '600px',
        border: '1px solid #3e3e42',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#1e1e1e',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Compact Toolbar - En Üstte Yapışık - Dark Theme */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: '#2d2d2d',
          borderRadius: '12px 12px 0 0',
          padding: '10px 14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          borderBottom: '1px solid #3e3e42',
          flexShrink: 0,
          zIndex: 1,
        }}>
          {/* Workflow Başlığı - Compact */}
          {workflowData?.workflow?.desc && (
            <>
              <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#e5e7eb',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {workflowData.workflow.filename}
              </div>
              <div style={{ width: 1, height: 24, background: '#3e3e42' }}></div>
            </>
          )}
          
          {/* Zoom Kontrolleri - Compact */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button 
              onClick={handleZoomOut} 
              title="Uzaklaştır (Ctrl + Scroll)" 
              style={compactButtonStyle}
              onMouseEnter={(e) => { 
                e.currentTarget.style.background = '#3e3e42';
                e.currentTarget.style.color = '#e5e7eb';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              −
            </button>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', minWidth: 42, textAlign: 'center', userSelect: 'none' }}>
              {zoomLevel}%
            </div>
            <button 
              onClick={handleZoomIn} 
              title="Yakınlaştır (Ctrl + Scroll)" 
              style={compactButtonStyle}
              onMouseEnter={(e) => { 
                e.currentTarget.style.background = '#3e3e42';
                e.currentTarget.style.color = '#e5e7eb';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              +
            </button>
          </div>

          <div style={{ width: 1, height: 24, background: '#3e3e42' }}></div>

          {/* Utility Buttons */}
          <button 
            onClick={handleFitScreen} 
            title="Ekrana Sığdır" 
            style={iconButtonStyle}
            onMouseEnter={(e) => { 
              e.currentTarget.style.background = '#3e3e42';
              e.currentTarget.style.color = '#e5e7eb';
            }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
            </svg>
          </button>

          <button 
            onClick={handleZoomReset} 
            title="Sıfırla (100%)" 
            style={iconButtonStyle}
            onMouseEnter={(e) => { 
              e.currentTarget.style.background = '#3e3e42';
              e.currentTarget.style.color = '#e5e7eb';
            }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
              <path d="M21 3v5h-5"></path>
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
              <path d="M3 21v-5h5"></path>
            </svg>
          </button>

          <button 
            onClick={() => setShowRuler(!showRuler)} 
            title={showRuler ? "Cetveli Gizle" : "Cetveli Göster"}
            style={{...iconButtonStyle, opacity: showRuler ? 1 : 0.5}}
            onMouseEnter={(e) => { 
              e.currentTarget.style.background = '#3e3e42';
              e.currentTarget.style.color = '#e5e7eb';
            }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16v4H4z"></path>
              <line x1="8" y1="4" x2="8" y2="8"></line>
              <line x1="12" y1="4" x2="12" y2="8"></line>
              <line x1="16" y1="4" x2="16" y2="8"></line>
            </svg>
          </button>

          {/* Spacer */}
          <div style={{ flex: 1 }}></div>

          {/* Development Mode Button - Compact */}
          <button
            onClick={() => setIsDevelopmentMode(true)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.35)',
              transition: 'all 0.2s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.35)';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
            </svg>
            Geliştir
          </button>
        </div>

        {/* Graph Container with Rulers */}
        <div style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          background: '#1e1e1e',
        }}>
          {/* Rulers - Dark Theme */}
          {showRuler && (
            <>
              {/* Corner Box */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 30,
                height: 20,
                background: '#2d2d2d',
                borderRight: '1px solid #3e3e42',
                borderBottom: '1px solid #3e3e42',
                zIndex: 2,
              }}></div>

              {/* Horizontal Ruler - Üstte */}
              <div 
                id="top-ruler-preview"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 30,
                  right: 0,
                  height: 20,
                  background: '#2d2d2d',
                  borderBottom: '1px solid #3e3e42',
                  zIndex: 1,
                  fontSize: 10,
                  color: '#ffffff',
                  fontFamily: 'monospace',
                  userSelect: 'none',
                  overflow: 'visible',
                  pointerEvents: 'none',
                }}
              />

              {/* Vertical Ruler - Solda */}
              <div 
                id="left-ruler-preview"
                style={{
                  position: 'absolute',
                  top: 20,
                  left: 0,
                  bottom: 0,
                  width: 30,
                  background: '#2d2d2d',
                  borderRight: '1px solid #3e3e42',
                  zIndex: 1,
                  fontSize: 10,
                  color: '#ffffff',
                  fontFamily: 'monospace',
                  userSelect: 'none',
                  overflow: 'visible',
                  pointerEvents: 'none',
                }}
              />
            </>
          )}
          
          {/* Flowchart Preview - Read-only */}
          <div
            ref={previewContainerRef}
            style={{
              position: 'absolute',
              top: showRuler ? 20 : 0,
              left: showRuler ? 30 : 0,
              right: 0,
              bottom: 0,
              overflow: 'hidden',
              background: '#ffffff',
              zIndex: 1,
            }}
          />
        </div>
      </div>

      {/* Development Screen Component */}
      {isDevelopmentMode && (
        <FlowChartDevelopmentScreen
          key={workflowData?.workflow?.id || 'default'}
          workflowData={workflowData}
          onClose={() => setIsDevelopmentMode(false)}
        />
      )}
    </>
  );
};

export default FlowChart; 
